import React from 'react';
import {
  getInterventionDisplayLines,
  getInterventionPhaseUiKey,
  type InstanceRow,
} from '../../components/interventions/instanceModel';
import { interventionPhaseClass, interventionPhaseLabel, vehicleStateLabel, type MainMapView } from '../mapUtils';
import { Vehicule } from '../../../../shared/types/game';

interface InterventionPhaseCounts {
  wait: number;
  travel: number;
  site: number;
}

interface InterventionsViewProps {
  filteredInterventions: InstanceRow[];
  activeInterventions: InstanceRow[];
  interventionPhaseCounts: InterventionPhaseCounts;
  selectedIntervention: InstanceRow | null;
  selectedDisplay: { headline: string; subline: string } | null;
  setSelectedInterventionId: (id: number) => void;
  getInterventionPriority: (inv: InstanceRow) => number;
  setInterventionPriority: (inv: InstanceRow, priority: number) => void;
  selectedInterventionVehicleRows: Vehicule[];
  setMainView: (view: MainMapView) => void;
  interventionSearch: string;
  setInterventionSearch: (v: string) => void;
  interventionPhaseFilter: 'all' | 'wait' | 'travel' | 'site';
  setInterventionPhaseFilter: (v: 'all' | 'wait' | 'travel' | 'site') => void;
  interventionSort: 'priority' | 'newest' | 'oldest';
  setInterventionSort: (v: 'priority' | 'newest' | 'oldest') => void;
  setManageId: (id: number | null) => void;
  openDedicatedMainView: (view: MainMapView) => void;
}

const InterventionsView: React.FC<InterventionsViewProps> = ({
  filteredInterventions,
  activeInterventions,
  interventionPhaseCounts,
  selectedIntervention,
  selectedDisplay,
  setSelectedInterventionId,
  getInterventionPriority,
  setInterventionPriority,
  selectedInterventionVehicleRows,
  setMainView,
  interventionSearch,
  setInterventionSearch,
  interventionPhaseFilter,
  setInterventionPhaseFilter,
  interventionSort,
  setInterventionSort,
  setManageId,
  openDedicatedMainView,
}) => (
  <div className="grid h-full min-h-0 grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.5fr),minmax(0,1fr)]">
    <section className="min-h-0 rounded-lg border border-slate-700/75 bg-slate-900/90 p-3 shadow-xl shadow-black/30">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-red-200">
          Synoptique interventions
        </h2>
        <div className="flex items-center gap-1.5">
          <span className="rounded border border-red-500/45 bg-red-500/15 px-2 py-1 text-[10px] font-semibold text-red-100">
            {filteredInterventions.length} / {activeInterventions.length}
          </span>
          <button
            type="button"
            onClick={() => openDedicatedMainView('interventions')}
            className="rounded border border-slate-600/70 bg-slate-800/70 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-200 hover:bg-slate-700/80"
          >
            Ouvrir en page
          </button>
          <button
            type="button"
            onClick={() => setMainView('dispatch')}
            className="rounded border border-red-500/45 bg-red-600/20 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-red-100 hover:bg-red-600/30"
          >
            Gestion dispatch
          </button>
        </div>
      </div>

      <div className="mb-3 grid grid-cols-2 gap-2 text-[11px] xl:grid-cols-6">
        <div className="rounded border border-red-500/35 bg-red-950/20 p-2">
          <p className="text-[10px] uppercase tracking-wide text-red-300">Actives</p>
          <p className="mt-1 text-lg font-semibold text-red-100">{activeInterventions.length}</p>
        </div>
        <div className="rounded border border-red-500/35 bg-red-950/20 p-2">
          <p className="text-[10px] uppercase tracking-wide text-red-300">Attente</p>
          <p className="mt-1 text-lg font-semibold text-red-100">{interventionPhaseCounts.wait}</p>
        </div>
        <div className="rounded border border-amber-500/35 bg-amber-950/20 p-2">
          <p className="text-[10px] uppercase tracking-wide text-amber-300">Transit</p>
          <p className="mt-1 text-lg font-semibold text-amber-100">{interventionPhaseCounts.travel}</p>
        </div>
        <div className="rounded border border-emerald-500/35 bg-emerald-950/20 p-2">
          <p className="text-[10px] uppercase tracking-wide text-emerald-300">Sur site</p>
          <p className="mt-1 text-lg font-semibold text-emerald-100">{interventionPhaseCounts.site}</p>
        </div>
        <div className="rounded border border-sky-500/35 bg-sky-950/20 p-2">
          <p className="text-[10px] uppercase tracking-wide text-sky-300">Unités engagées</p>
          <p className="mt-1 text-lg font-semibold text-sky-100">
            {activeInterventions.reduce((sum, inv) => sum + Number(inv.dispatch_total || 0), 0)}
          </p>
        </div>
        <div className="rounded border border-slate-500/35 bg-slate-950/20 p-2">
          <p className="text-[10px] uppercase tracking-wide text-slate-300">Arrivée estimée</p>
          <p className="mt-1 text-lg font-semibold text-slate-100">
            {Math.max(
              0,
              ...activeInterventions.map((inv) => Math.ceil(Number(inv.travel_estimate_sec || 0) / 60))
            )} min
          </p>
        </div>
      </div>

      <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-4">
        <input
          type="search"
          value={interventionSearch}
          onChange={(e) => setInterventionSearch(e.target.value)}
          placeholder="Rechercher numéro / mission / adresse..."
          className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1.5 text-[11px] text-slate-100 placeholder:text-slate-500 outline-none focus:border-red-500/60 md:col-span-2"
        />
        <select
          value={interventionPhaseFilter}
          onChange={(e) => setInterventionPhaseFilter(e.target.value as 'all' | 'wait' | 'travel' | 'site')}
          className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1.5 text-[11px] text-slate-100 outline-none focus:border-red-500/60"
        >
          <option value="all">Phase: toutes</option>
          <option value="wait">En attente</option>
          <option value="travel">En transit</option>
          <option value="site">Sur site</option>
        </select>
        <select
          value={interventionSort}
          onChange={(e) => setInterventionSort(e.target.value as 'priority' | 'newest' | 'oldest')}
          className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1.5 text-[11px] text-slate-100 outline-none focus:border-red-500/60"
        >
          <option value="priority">Tri: priorité</option>
          <option value="newest">Tri: plus récent</option>
          <option value="oldest">Tri: plus ancien</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-md border border-slate-700/70 bg-slate-950/85">
        <div className="grid grid-cols-[90px,minmax(0,1.1fr),minmax(0,0.95fr),90px,90px,100px,120px,110px] border-b border-slate-700/70 bg-slate-900/70 px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400">
          <span>N°</span>
          <span>Mission</span>
          <span>Adresse</span>
          <span className="text-center">Phase</span>
          <span className="text-center">Priorité</span>
          <span className="text-center">Unités</span>
          <span className="text-right">Créée le</span>
          <span className="text-center">Action</span>
        </div>
        <div className="max-h-[56vh] overflow-y-auto text-[11px]">
          {filteredInterventions.map((inv) => {
            const lines = getInterventionDisplayLines(inv, 'Intervention');
            const phase = getInterventionPhaseUiKey(inv);
            const priority = getInterventionPriority(inv);
            return (
              <div
                key={inv.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedInterventionId(inv.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedInterventionId(inv.id);
                  }
                }}
                className={`grid w-full grid-cols-[90px,minmax(0,1.1fr),minmax(0,0.95fr),90px,90px,100px,120px,110px] items-center gap-1 border-b border-slate-800/65 px-2 py-2 text-left last:border-b-0 ${
                  selectedIntervention?.id === inv.id ? 'bg-red-950/20' : 'hover:bg-slate-800/70'
                }`}
              >
                <span className="truncate font-semibold text-slate-200">#{inv.numero_intervention || inv.id}</span>
                <span className="truncate text-slate-100">{lines.headline}</span>
                <span className="truncate text-slate-400">{lines.subline || 'Adresse non précisée'}</span>
                <span className={`mx-auto w-fit rounded border px-2 py-0.5 text-[10px] font-semibold ${interventionPhaseClass(phase)}`}>
                  {interventionPhaseLabel(phase)}
                </span>
                <span className="mx-auto">
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={priority}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setInterventionPriority(inv, Number(e.target.value))}
                    className="w-14 rounded border border-red-500/45 bg-slate-900/95 px-1.5 py-0.5 text-center text-[10px] font-semibold text-red-200 outline-none focus:border-red-400"
                  />
                </span>
                <span className="text-center text-slate-300">
                  {Number(inv.dispatch_total || 0)} ({Number(inv.dispatch_sur_place || 0)} sur site)
                </span>
                <span className="text-right text-[10px] text-slate-400">
                  {inv.date_creation ? new Date(inv.date_creation).toLocaleString('fr-FR') : '—'}
                </span>
                <span className="text-center">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedInterventionId(inv.id);
                      setManageId(inv.id);
                    }}
                    className="rounded border border-emerald-500/50 bg-emerald-600/20 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-100 hover:bg-emerald-600/30"
                  >
                    Engager
                  </button>
                </span>
              </div>
            );
          })}
          {filteredInterventions.length === 0 ? (
            <p className="px-2 py-5 text-center text-slate-500">
              Aucune intervention correspondant aux filtres.
            </p>
          ) : null}
        </div>
      </div>
    </section>

    <section className="grid min-h-0 grid-rows-[auto,1fr] gap-3">
      <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
        <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400">Intervention sélectionnée</p>
        <p className="mt-1 truncate text-sm font-semibold text-slate-100">{selectedDisplay?.headline || 'Aucune intervention'}</p>
        <p className="mt-0.5 truncate text-[11px] text-slate-400">{selectedDisplay?.subline || '—'}</p>
        {selectedIntervention ? (
          <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
            <div className="rounded border border-slate-700/70 bg-slate-950/70 p-2">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Phase</p>
              <p className="mt-1 text-slate-100">{interventionPhaseLabel(getInterventionPhaseUiKey(selectedIntervention))}</p>
            </div>
            <div className="rounded border border-slate-700/70 bg-slate-950/70 p-2">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Priorité</p>
              <p className="mt-1 text-slate-100">{getInterventionPriority(selectedIntervention)}</p>
            </div>
            <div className="rounded border border-slate-700/70 bg-slate-950/70 p-2">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">En route</p>
              <p className="mt-1 text-slate-100">{Number(selectedIntervention.dispatch_en_route || 0)}</p>
            </div>
            <div className="rounded border border-slate-700/70 bg-slate-950/70 p-2">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Sur place</p>
              <p className="mt-1 text-slate-100">{Number(selectedIntervention.dispatch_sur_place || 0)}</p>
            </div>
          </div>
        ) : null}
        {selectedIntervention ? (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => {
                if (!selectedIntervention) return;
                setManageId(selectedIntervention.id);
              }}
              className="rounded border border-emerald-500/55 bg-emerald-600/25 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-100 hover:bg-emerald-600/35"
            >
              Ouvrir engagement détaillé
            </button>
            <button
              type="button"
              onClick={() => setMainView('resources')}
              className="rounded border border-sky-500/55 bg-sky-600/20 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-sky-100 hover:bg-sky-600/30"
            >
              Voir ressources
            </button>
          </div>
        ) : null}
      </div>
      <div className="min-h-0 rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
        <p className="mb-2 text-[10px] uppercase tracking-[0.12em] text-slate-400">Unités engagées</p>
        <div className="max-h-[48vh] space-y-1.5 overflow-y-auto text-[11px]">
          {selectedInterventionVehicleRows.length > 0 ? (
            selectedInterventionVehicleRows.map((v) => (
              <div key={v.id} className="rounded border border-slate-700/70 bg-slate-950/70 px-2 py-1.5">
                <p className="truncate font-semibold text-slate-100">{v.nom || `Unité ${v.id}`}</p>
                <p className="text-[10px] text-slate-400">
                  {String(v.type || 'Type inconnu')} · {vehicleStateLabel(v.statut)}
                </p>
              </div>
            ))
          ) : (
            <p className="text-slate-500">Aucune unité engagée sur cette intervention.</p>
          )}
        </div>
      </div>
    </section>
  </div>
);

export default InterventionsView;
